import { useCallback, useEffect, useRef, useState } from 'react';

import { MODAL_WIDTHS } from '@/shared/lib/constants';
import { sortTags } from '@/shared/lib/sort';
import { BaseModal } from '@/shared/ui/BaseModal';
import { ExperimentTagInput } from '@/shared/ui/ExperimentTagInput';

const TITLE = 'DAG 태그 수정';

const normalizeTags = (tags: string[]) => tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
const areTagSetsEqual = (leftTags: string[], rightTags: string[]) => {
  const leftTagSet = new Set(leftTags);
  const rightTagSet = new Set(rightTags);

  if (leftTagSet.size !== rightTagSet.size) {
    return false;
  }

  for (const tag of leftTagSet) {
    if (!rightTagSet.has(tag)) {
      return false;
    }
  }

  return true;
};

type DagTagEditModalProps = {
  open: boolean;
  tags: string[];
  onClose: () => void;
  onSubmit: (tags: string[]) => void | Promise<void>;
  confirmLoading?: boolean;
};

export const DagTagEditModal = ({ open, tags, onClose, onSubmit, confirmLoading = false }: DagTagEditModalProps) => {
  const [editingTags, setEditingTags] = useState<string[]>([]);
  const prevOpenRef = useRef(false);

  const handleSubmit = useCallback(async () => {
    await onSubmit(normalizeTags(editingTags));
  }, [editingTags, onSubmit]);

  const hasChanges = useCallback(() => {
    const currentTags = normalizeTags(editingTags);
    const originalTags = normalizeTags(tags);

    return !areTagSetsEqual(currentTags, originalTags);
  }, [editingTags, tags]);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      const sortedTags = tags.length > 0 ? sortTags(tags) : [''];
      setEditingTags(sortedTags);
    }

    prevOpenRef.current = open;
  }, [open, tags]);

  const isSaveButtonDisabled = editingTags.length === 1 && editingTags[0].trim() === '';

  return (
    <BaseModal
      title={TITLE}
      open={open}
      onClose={onClose}
      onOk={handleSubmit}
      okText="저장"
      cancelText="취소"
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: !hasChanges() || isSaveButtonDisabled }}
      width={MODAL_WIDTHS.STANDARD_FORM}
      centered>
      <ExperimentTagInput
        value={editingTags}
        placeholder="Tag name을 입력해 주세요"
        onChange={setEditingTags}
        scrollable
        maxHeight="468px"
      />
    </BaseModal>
  );
};
